/**
 * The Knowledge screen's logic, kept out of the component so it can be tested (fix-22).
 *
 * The desk is the authority on every rule here — it refuses a bad save whatever this file says. These checks exist
 * so the operator sees the refusal before he presses Save, in the same words the desk would use.
 */
import type { KnowledgeArticle, KnowledgeFields, KnowledgeRow } from './api';

export type View = 'yours' | 'website' | 'retired';

/** Which tab an article belongs on. Retired first: a retired website page is retired, not a page to read. */
export function viewOf(a: Pick<KnowledgeRow, 'status' | 'editable'>): View {
  if (a.status === 'retired') return 'retired';
  return a.editable ? 'yours' : 'website';
}

/** The rows for one tab, filtered by a search over title and slug. */
export function filterArticles(rows: KnowledgeRow[], view: View, query: string): KnowledgeRow[] {
  const q = query.trim().toLowerCase();
  return rows.filter((a) => viewOf(a) === view
    && (!q || a.title.toLowerCase().includes(q) || a.slug.includes(q)));
}

export const EMPTY: KnowledgeFields = { title: '', body_md: '', audience: 'internal', weight: 150, supersedes_id: null, reason: '' };

/** The editor's starting fields for an article (the reason always starts empty: it is this save's). */
export function fieldsOf(a: KnowledgeArticle | null): KnowledgeFields {
  if (!a) return { ...EMPTY };
  return { title: a.title, body_md: a.body_md, audience: a.audience, weight: a.weight, supersedes_id: a.supersedes_id, reason: '' };
}

/** What the desk would refuse, in its words; null when it would save. */
export function problem(f: KnowledgeFields, selfId: number | null): string | null {
  if (!f.title.trim()) return 'an article needs a title';
  if (f.title.trim().length > 200) return 'a title is at most 200 characters';
  if (!f.body_md.trim()) return 'an article needs a body';
  if (!Number.isInteger(f.weight) || f.weight < 0 || f.weight > 300) return 'weight is a whole number from 0 to 300';
  if (selfId !== null && f.supersedes_id === selfId) return 'an article cannot supersede itself';
  if (!f.reason.trim()) return 'every save needs a reason — it is kept with the version';
  return null;
}

/** Has anything but the reason changed? A save with nothing to save is not offered. */
export function changed(f: KnowledgeFields, a: KnowledgeArticle | null): boolean {
  const base = fieldsOf(a);
  return (Object.keys(base) as (keyof KnowledgeFields)[]).some((k) => k !== 'reason' && base[k] !== f[k]);
}

/** What an article may supersede: any live article but itself, by title. */
export function supersedeOptions(rows: KnowledgeRow[], selfId: number | null): KnowledgeRow[] {
  return rows.filter((a) => a.id !== selfId && a.status !== 'retired').sort((x, y) => x.title.localeCompare(y.title));
}

/** fix-57: the operator article holding the drafter's voice and house rules — in every prompt, locked but for its body. */
export const INSTRUCTIONS_SLUG = 'drafter-instructions';

/** The article the screen opens on when none is chosen: the Drafter instructions, if the list has them. */
export function defaultArticle(rows: KnowledgeRow[]): number | null {
  return rows.find((r) => r.slug === INSTRUCTIONS_SLUG)?.id ?? null;
}

/** Dollars per draft, or what the model costs per million tokens when it has drafted nothing yet. */
export function costLine(m: { per_draft?: number | null; drafts_counted?: number; price?: { in: number; out: number } | null }): string {
  if (m.per_draft != null) return `$${m.per_draft.toFixed(3)} a draft (${m.drafts_counted} drafts, last 7 days on this model)`;
  return m.price ? `no drafts on this model yet · $${m.price.in} in / $${m.price.out} out per million tokens` : 'no price';
}

/**
 * What the editor does when the article is fetched again (16 Oct 2026). useResource refetches on every return to the
 * tab, and each fetch is a new object — so reloading the form on every fetch threw away unsaved edits.
 *
 *   load    — fill the form from the fetched article: the first read, another article, or nothing typed to lose
 *   keep    — the same saved copy, or there is nothing fetched: leave the form alone
 *   notice  — the saved copy changed while the form holds edits: keep the edits and say so
 *
 * `base` is the article the form was last loaded from; dirtiness is measured against it, not the fetched copy.
 */
export function reconcile(
  base: KnowledgeArticle | null, form: KnowledgeFields, fetched: KnowledgeArticle | null,
): 'load' | 'keep' | 'notice' {
  if (!fetched) return 'keep';
  if (!base || base.id !== fetched.id) return 'load';
  if (base.updated_at === fetched.updated_at) return 'keep';
  if (!changed(form, base)) return 'load';
  // The desk already holds what the form holds — this editor's own save, read back.
  if (!changed(form, fetched)) return 'load';
  return 'notice';
}
